/**
 * Meta Pixel + Conversions API Client-Side Integration
 *
 * This module handles:
 * 1. Meta Pixel (client-side tracking via fbq)
 * 2. Sending events to backend for Conversions API (server-side)
 *
 * Events tracked:
 * - Lead (signup/account creation)
 * - StartTrial (free trial started)
 * - Subscribe (subscription activated)
 * - Purchase (payment completed)
 * - AddPaymentInfo (payment method added)
 * - Schedule (call scheduled)
 * - ViewContent (page views)
 */

// Extend window type for Facebook Pixel
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';
const API_URL = import.meta.env.VITE_API_URL || '';

// Check if pixel is configured
const isPixelConfigured = (): boolean => !!PIXEL_ID;

/**
 * Initialize Meta Pixel
 * Call this once in your app initialization (e.g., App.tsx or main.tsx)
 */
export function initMetaPixel(): void {
  if (!isPixelConfigured()) {
    console.warn('[MetaPixel] Pixel ID not configured');
    return;
  }

  // Skip if already initialized
  if (window.fbq) {
    return;
  }

  // Facebook Pixel base code
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: HTMLScriptElement, s?: HTMLScriptElement) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
    s.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');

  console.log('[MetaPixel] Initialized with Pixel ID:', PIXEL_ID);
}

/**
 * Get Facebook cookies for server-side event matching
 */
function getFbCookies(): { fbc?: string; fbp?: string } {
  const cookies: { fbc?: string; fbp?: string } = {};

  // Get _fbc cookie (click ID)
  const fbcMatch = document.cookie.match(/_fbc=([^;]+)/);
  if (fbcMatch) {
    cookies.fbc = fbcMatch[1];
  }

  // Get _fbp cookie (browser ID)
  const fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
  if (fbpMatch) {
    cookies.fbp = fbpMatch[1];
  }

  return cookies;
}

/**
 * Send event to backend for Conversions API
 */
async function sendServerEvent(eventName: string, userData: Record<string, any>, customData?: Record<string, any>): Promise<void> {
  if (!API_URL) {
    console.warn('[MetaPixel] API URL not configured for server events');
    return;
  }

  const { fbc, fbp } = getFbCookies();

  try {
    await fetch(`${API_URL}/api/meta/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        userData: {
          ...userData,
          fbc,
          fbp,
          eventSourceUrl: window.location.href,
        },
        customData,
      }),
    });
  } catch (error) {
    console.error('[MetaPixel] Failed to send server event:', error);
  }
}

// ========== Event Tracking Functions ==========

/**
 * Track Lead event (user signup/account creation)
 */
export function trackLead(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'Signup',
    });
  }

  // Server-side Conversions API
  sendServerEvent('Lead', userData);
}

/**
 * Track StartTrial event
 */
export function trackStartTrial(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}, trialPlan?: string): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'StartTrial', {
      content_name: trialPlan || 'Free Trial',
    });
  }

  // Server-side Conversions API
  sendServerEvent('StartTrial', userData, { trialPlan });
}

/**
 * Track Subscribe event
 */
export function trackSubscribe(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}, subscriptionData: {
  planName?: string;
  value?: number;
  currency?: string;
}): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'Subscribe', {
      content_name: subscriptionData.planName,
      value: subscriptionData.value,
      currency: subscriptionData.currency || 'USD',
    });
  }

  // Server-side Conversions API
  sendServerEvent('Subscribe', userData, subscriptionData);
}

/**
 * Track Purchase event (payment completed)
 */
export function trackPurchase(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}, purchaseData: {
  value: number;
  currency?: string;
  orderId?: string;
  contentName?: string;
}): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: purchaseData.value,
      currency: purchaseData.currency || 'USD',
      content_name: purchaseData.contentName,
    });
  }

  // Server-side Conversions API
  sendServerEvent('Purchase', userData, purchaseData);
}

/**
 * Track AddPaymentInfo event
 */
export function trackAddPaymentInfo(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'AddPaymentInfo');
  }

  // Server-side Conversions API
  sendServerEvent('AddPaymentInfo', userData);
}

/**
 * Track Schedule event (call scheduled)
 */
export function trackSchedule(userData: {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}, scheduledDate?: string): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'Schedule', {
      content_name: scheduledDate ? `Scheduled: ${scheduledDate}` : 'Call Scheduled',
    });
  }

  // Server-side Conversions API
  sendServerEvent('Schedule', userData, { scheduledDate });
}

/**
 * Track ViewContent event
 */
export function trackViewContent(contentData?: {
  contentName?: string;
  contentCategory?: string;
}, userData?: {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userId?: string;
}): void {
  // Client-side pixel
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: contentData?.contentName,
      content_category: contentData?.contentCategory,
    });
  }

  // Server-side (only if user data provided)
  if (userData?.email) {
    sendServerEvent('ViewContent', userData, contentData);
  }
}

/**
 * Track PageView (automatic with pixel init, but can be called manually for SPAs)
 */
export function trackPageView(): void {
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * Track custom event
 */
export function trackCustomEvent(eventName: string, parameters?: Record<string, any>): void {
  if (window.fbq) {
    window.fbq('trackCustom', eventName, parameters);
  }
}
